/**
 * `useTrack()` returns a stable `track(opts)` function. Each call materialises
 * an `AnalyticsEnvelope` and enqueues it. When no relay is configured (local
 * dev / preview), envelopes go to `console.debug` so devs can see emissions
 * without provisioning the backend.
 */
import { useCallback } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/lib/cerberus/useAuth'
import { ulid } from '@/lib/ulid'
import { useAnalyticsContext } from './AnalyticsProvider'
import { shouldSample } from './sampling'
import {
  analyticsSourceForPath,
  canonicalActionForSource,
  noteUnknownBrandDrop,
  schemaVersionForAction,
  schemaVersionForSource,
} from './source'
import type { AnalyticsEnvelope, Screen, TrackOptions } from './types'

const PATH_TO_SCREEN: Array<[RegExp, Screen]> = [
  [/^\/$/, 'home'],
  [/^\/bills/, 'bills'],
  [/^\/historico/, 'historico'],
  [/^\/insights/, 'insights'],
  [/^\/profile/, 'profile'],
  [/^\/onboarding/, 'onboarding'],
  [/^\/login/, 'login'],
  // `/reset` is the password-reset confirmation page; like `/login` it is
  // a pre-auth surface and the early-return below means no envelope is
  // actually emitted (`status !== 'authed'`). Mapped to `'login'` because
  // the Screen taxonomy has no dedicated reset value and there is no
  // analytics consumer that distinguishes them today.
  [/^\/reset/, 'login'],
]

function pathToScreen(pathname: string): Screen {
  for (const [re, s] of PATH_TO_SCREEN) if (re.test(pathname)) return s
  return 'home'
}

export function useTrack(): (opts: TrackOptions) => void {
  const ctx = useAnalyticsContext()
  const { user, activeOrgId, status } = useAuth()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  return useCallback(
    (opts: TrackOptions) => {
      if (status !== 'authed' || !user?.id || !activeOrgId) {
        // §7 of the spec: only emit once authed. Pre-auth surfaces (login,
        // signup) intentionally do not generate analytics envelopes.
        return
      }
      const sourceSystem = analyticsSourceForPath(pathname)
      if (sourceSystem === null) {
        // The Origin serving this bundle could not be attributed to a
        // product. Emitting anyway would write the row under whatever
        // name we guessed — i.e. another product's rows inside the
        // Cuidari lake. Drop it, loudly, and count the drop.
        noteUnknownBrandDrop()
        return
      }
      const screen = pathToScreen(pathname)
      const action = canonicalActionForSource(opts.action, sourceSystem, {
        screen,
        metadata: opts.metadata,
      })
      const schemaVersion = schemaVersionForSource(
        opts.schema_version ?? schemaVersionForAction(action, sourceSystem),
        sourceSystem,
      )
      // §4.7 sampling — drop before envelope materialisation so high-volume
      // story-view envelopes don't churn the queue heap. Lookup table lives
      // in `./sampling.ts` to keep the rate decisions auditable.
      if (!shouldSample(schemaVersion)) return
      const envelope: AnalyticsEnvelope = {
        schema_version: schemaVersion,
        event_id: ulid(),
        emitted_at: new Date().toISOString(),
        source_system: sourceSystem,
        sensitivity: 'internal',
        user_id: user.id,
        tenant_id: activeOrgId,
        session_id: ctx?.sessionId ?? 'no-session',
        screen,
        component_id: opts.component_id,
        component_type: opts.component_type,
        action,
        ...(opts.metadata ? { metadata: opts.metadata } : {}),
      }

      if (!ctx?.queue) {
        if (typeof console !== 'undefined') {
          console.debug('[analytics] (no relay)', envelope.schema_version, envelope)
        }
        return
      }
      ctx.queue.enqueue(envelope)
    },
    [ctx, user?.id, activeOrgId, status, pathname],
  )
}
